import type {
  ProductAvailabilityTierPatchRequestType,
  SupplierProductGetResponseChildType,
} from "@sellar-io/api-v2/types";
import { Switch, TableSimple, InputCheckbox } from "@sellar-io/components";
import { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import { Alert } from "../../../../components/alert/Alert";
import { useAvailabilityTierList } from "../../../../hooks/api/v2/availabilityTier/useAvailabilityTierList";
import { useProductAvailabilityTierPatch } from "../../../../hooks/api/v2/productAvailabilityTier/useProductAvailabilityTierPatch";
import { useProductStatusUpdate } from "../../../../hooks/api/v2/product/useProductStatusUpdate";

import type { ProductItemAvailabilitySectionHandle } from "./ProductItemAvailabilitySection";

export const AdvancedProductItemAvailabilitySection = forwardRef<
  ProductItemAvailabilitySectionHandle,
  { product: SupplierProductGetResponseChildType }
>(({ product }, ref) => {
  const [isHidden, setIsHidden] = useState(!product.active);
  const [localSelectedIds, setLocalSelectedIds] = useState<number[] | null>(
    null,
  );

  const productStatusUpdateMutation = useProductStatusUpdate(product.id);
  const productAvailabilityTierPatchMutation =
    useProductAvailabilityTierPatch();

  const availabilityTierListQuery = useAvailabilityTierList({
    limit: 50,
    query: [
      {
        type: "ProductId",
        modifier: "EQUALS",
        value: product.id,
      },
    ],
  });

  const availabilityTiers = useMemo(
    () => availabilityTierListQuery.data?.pages.flat() ?? [],
    [availabilityTierListQuery.data],
  );

  const serverSelectedIds = useMemo(() => {
    return availabilityTiers
      .filter((tier) => tier.ProductAvailabilityTiers[0]?.active === true)
      .map((tier) => tier.id);
  }, [availabilityTiers]);

  const selectedIds = localSelectedIds ?? serverSelectedIds;

  const toggleAvailabilityTier = (id: number) => {
    setLocalSelectedIds((prev) => {
      const currentSelectedIds = prev ?? serverSelectedIds;

      const hasId = currentSelectedIds.includes(id);

      return hasId
        ? currentSelectedIds.filter((sid) => sid !== id)
        : [...currentSelectedIds, id];
    });
  };

  const shouldUpdate =
    isHidden !== !product.active || localSelectedIds !== null;

  const save = async () => {
    if (!shouldUpdate) {
      return;
    }

    if (isHidden) {
      if (product.active) {
        await productStatusUpdateMutation.mutateAsync({ active: false });
      }
      return;
    }

    const promises: Promise<unknown>[] = [];

    if (!product.active) {
      promises.push(productStatusUpdateMutation.mutateAsync({ active: true }));
    }

    const patchPayload: ProductAvailabilityTierPatchRequestType =
      availabilityTiers.flatMap(
        (tier): ProductAvailabilityTierPatchRequestType => {
          const existingRecord = tier.ProductAvailabilityTiers[0];
          const isSelected = selectedIds.includes(tier.id);

          if (existingRecord?.active === isSelected) return [];
          if (!existingRecord && !isSelected) return [];

          if (existingRecord) {
            return [
              {
                id: existingRecord.id,
                value: { id: existingRecord.id, active: isSelected },
                action: "PUT" as const,
              },
            ];
          }

          return [
            {
              value: {
                ProductId: product.id,
                AvailabilityTierId: tier.id,
                active: true,
              },
              action: "POST" as const,
            },
          ];
        },
      );

    if (patchPayload.length > 0) {
      promises.push(
        productAvailabilityTierPatchMutation.mutateAsync(patchPayload),
      );
    }

    await Promise.all(promises);
    setLocalSelectedIds(null);
  };

  useImperativeHandle(ref, () => ({ save }));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <Switch checked={isHidden} onChange={setIsHidden} />
        <span className="text-zinc-700">
          Hide product across all product catalogues
        </span>
      </div>
      {isHidden && (
        <Alert
          title="This product item is hidden"
          message="Toggle off the switch to edit this item's visibility per product catalogue"
        />
      )}
      <TableSimple
        className="max-h-75 overflow-auto"
        roundedTop
        data={availabilityTiers}
        getRowId={(tier) => tier.id}
        gridClasses="grid-cols-[1fr_80px]"
        columns={[
          {
            id: "catalogue",
            header: "Product catalogue",
            cell: ({ row }) => row.original.name,
          },
          {
            id: "visible",
            header: "Visible",
            cell: ({ row }) => (
              <InputCheckbox
                aria-label={`Visible in ${row.original.name}`}
                required={false}
                checked={selectedIds.includes(row.original.id)}
                onChange={() => toggleAvailabilityTier(row.original.id)}
                disabled={isHidden}
              />
            ),
          },
        ]}
        isLoading={availabilityTierListQuery.isLoading}
        noResultsText="No product catalogues"
      />
    </div>
  );
});

AdvancedProductItemAvailabilitySection.displayName =
  "AdvancedProductItemAvailabilitySection";
